import { useEffect, useRef, type RefObject } from "react";

/**
 * Calls `onOutside` on a mousedown that lands outside every supplied ref.
 *
 * Uses `event.composedPath()` rather than `event.target` so it works inside a
 * Shadow DOM: events that bubble to `document` are retargeted to the shadow
 * host, which would make a naive `ref.contains(event.target)` check treat
 * clicks *inside* the shadow tree as outside. `composedPath()` includes the
 * real (pre-retarget) nodes, so the check is correct in both light and shadow DOM.
 *
 * The ref objects are assumed stable (from `useRef`); the listener is only
 * (re)subscribed when `enabled` changes.
 */
export function useClickOutside(
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  onOutside: () => void,
  enabled = true
): void {
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;

  useEffect(() => {
    if (!enabled) return;
    const handle = (e: MouseEvent) => {
      const path = e.composedPath();
      const inside = refs.some((r) => r.current && path.includes(r.current));
      if (!inside) onOutsideRef.current();
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
    // `refs` entries are stable useRef objects read at event time; only
    // `enabled` should drive (un)subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
